import axios from 'axios';
import { Coin, CoinDetail, MarketChart } from '../types';

// Use proxy in development, direct API in production
const API_BASE = import.meta.env.DEV ? '/api' : 'https://api.coingecko.com/api/v3';
const CACHE_DURATION = 120000; // 2 minutes (rate limit window)
const RATE_LIMIT_DELAY = 60000; // 60 seconds between requests (1 minute)

let lastRequestTime = 0;
let rateLimitUntil = 0; // Timestamp until which we should wait

const cache = new Map<string, { data: any; timestamp: number }>();

const getFromCache = (key: string) => {
  const item = cache.get(key);
  if (item && Date.now() - item.timestamp < CACHE_DURATION) {
    return item.data;
  }
  return null;
};

const setCache = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};



const fetchWithRetry = async <T>(
  url: string,
  params: any,
  maxRetries = 2,
  delay = 10000
): Promise<T> => {
  const isDev = import.meta.env.DEV;
  
  // Rate limiting: ensure minimum delay between requests
  const now = Date.now();
  
  // Check if we're in a rate limit cooldown period
  if (rateLimitUntil > now) {
    const waitTime = rateLimitUntil - now;
    console.log(`Rate limit cooldown: waiting ${waitTime}ms`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < RATE_LIMIT_DELAY) {
    const waitTime = RATE_LIMIT_DELAY - timeSinceLastRequest;
    console.log(`Rate limit: waiting ${waitTime}ms before next request`);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  lastRequestTime = Date.now();
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      // In development, always use proxy to avoid CORS issues
      if (isDev) {
        const response = await axios.get(url, {
          params,
          timeout: 30000,
          headers: {
            'Accept': 'application/json',
          },
        });
        return response.data;
      }
      
      // In production, use direct API
      const response = await axios.get(url, {
        params,
        timeout: 30000,
        headers: {
          'Accept': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.log(`Attempt ${i + 1} failed:`, error.message);
      
      // Handle rate limit specifically
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000; // 60 seconds default
        
        console.log(`Rate limit hit! Waiting ${waitTime}ms...`);
        
        // Set rate limit cooldown
        rateLimitUntil = Date.now() + waitTime;
        
        if (i === maxRetries - 1) {
          throw new Error('Rate limit exceeded - Please wait a moment and try again');
        }
        continue;
      }
      
      if (i === maxRetries - 1) throw error;
      
      // Don't retry on 4xx errors (except 429)
      if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 429) {
        throw error;
      }
      
      // Exponential backoff with longer delays
      const backoffDelay = delay * Math.pow(2, i);
      console.log(`Retrying in ${backoffDelay}ms...`);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
  throw new Error('Max retries exceeded');
};

export const getCoins = async (vs_currency = 'usd'): Promise<Coin[]> => {
  const cacheKey = `coins_${vs_currency}`;
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('Using cached coin data');
    return cached;
  }

  try {
    console.log(`Fetching coins for ${vs_currency}...`);
    const data = await fetchWithRetry(`${API_BASE}/coins/markets`, {
      vs_currency,
      order: 'market_cap_desc',
      per_page: 50,
      page: 1,
      sparkline: false,
      price_change_percentage: '24h,7d,30d',
    });
    
    // Ensure all fields have default values
    const safeData = data.map((coin: any) => ({
      id: coin.id,
      symbol: coin.symbol || '',
      name: coin.name || '',
      image: coin.image || '',
      current_price: coin.current_price || 0,
      price_change_percentage_24h: coin.price_change_percentage_24h || 0,
      market_cap: coin.market_cap || 0,
      total_volume: coin.total_volume || 0,
      circulating_supply: coin.circulating_supply || 0,
      high_24h: coin.high_24h || 0,
      low_24h: coin.low_24h || 0,
    }));
    
    setCache(cacheKey, safeData);
    console.log(`Successfully fetched ${safeData.length} coins`);
    return safeData;
  } catch (error: any) {
    console.error('Error fetching coins:', error);
    
    // If rate limited or network error, return cached data if available
    if (cached && (error.response?.status === 429 || error.response?.status === 401 || error.code === 'ERR_NETWORK')) {
      console.log('Using cached data due to API limit or network error');
      return cached;
    }
    
    // Provide more specific error messages
    if (error.code === 'ERR_NETWORK') {
      throw new Error('Network error - Please check your internet connection');
    } else if (error.response?.status === 429) {
      throw new Error('Rate limit exceeded - Please wait a moment and try again');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error - Please try again later');
    } else {
      throw new Error('Failed to fetch coin data');
    }
  }
};

export const getCoinDetail = async (id: string, vs_currency = 'usd'): Promise<CoinDetail> => {
  const cacheKey = `coin_${id}_${vs_currency}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const coin = await fetchWithRetry(`${API_BASE}/coins/${id}`, {
      localization: false,
      tickers: false,
      community_data: true,
      developer_data: true,
      vs_currency,
    });
    
    // Ensure all fields have default values
    const safeData = {
      id: coin.id,
      symbol: coin.symbol || '',
      name: coin.name || '',
      image: coin.image?.large || coin.image?.thumb || '',
      current_price: coin.market_data?.current_price?.[vs_currency] || 0,
      price_change_percentage_24h: coin.market_data?.price_change_percentage_24h || 0,
      market_cap: coin.market_data?.market_cap?.[vs_currency] || 0,
      total_volume: coin.market_data?.total_volume?.[vs_currency] || 0,
      circulating_supply: coin.market_data?.circulating_supply || 0,
      high_24h: coin.market_data?.high_24h?.[vs_currency] || 0,
      low_24h: coin.market_data?.low_24h?.[vs_currency] || 0,
      description: coin.description || { en: '' },
      genesis_date: coin.genesis_date || '',
      market_cap_rank: coin.market_cap_rank || 0,
      coingecko_rank: coin.coingecko_rank || 0,
      community_data: coin.community_data || { twitter_followers: 0, reddit_subscribers: 0 },
      developer_data: coin.developer_data || { forks: 0, stars: 0, subscribers: 0 },
    };
    
    setCache(cacheKey, safeData);
    return safeData;
  } catch (error: any) {
    console.error(`Error fetching coin detail for ${id}:`, error);
    
    // If rate limited or unauthorized, return cached data if available
    if (cached && (error.response?.status === 429 || error.response?.status === 401)) {
      console.log('Using cached coin data due to API limit');
      return cached;
    }
    
    throw new Error(`Failed to fetch coin details for ${id}`);
  }
};

export const getMarketChart = async (
  id: string,
  days: number = 7,
  vs_currency = 'usd'
): Promise<MarketChart> => {
  const cacheKey = `chart_${id}_${days}_${vs_currency}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const data = await fetchWithRetry(`${API_BASE}/coins/${id}/market_chart`, {
      vs_currency,
      days,
      interval: days > 7 ? 'daily' : 'hourly',
    });
    setCache(cacheKey, data);
    return data;
  } catch (error: any) {
    console.error(`Error fetching market chart for ${id}:`, error);
    // If rate limited or unauthorized, return cached data or generate mock data
    if (error.response?.status === 429 || error.response?.status === 401) {
      if (cached) {
        console.log('Using cached chart data due to API limit');
        return cached;
      }
      // Generate mock chart data for visualization
      console.log('Generating mock chart data');
      const now = Date.now();
      const interval = days <= 1 ? 3600000 : 86400000; // 1 hour or 1 day
      const points = days <= 1 ? 24 : days;
      const mockPrices = [];
      // Will be replaced with actual price in component
      const basePrice = 1; // Placeholder, will use actual coin price
      
      for (let i = points; i >= 0; i--) {
        const timestamp = now - (i * interval);
        // Generate realistic price variations
        const variation = (Math.random() - 0.5) * 0.1; // ±5% variation
        const price = basePrice * (1 + variation);
        mockPrices.push([timestamp, price]);
      }
      
      const mockChart = {
        prices: mockPrices,
        market_caps: [],
        total_volumes: [],
      };
      setCache(cacheKey, mockChart);
      return mockChart;
    }
    throw new Error(`Failed to fetch market chart for ${id}`);
  }
};

export const searchCoins = async (query: string): Promise<Coin[]> => {
  if (!query || query.length < 2) return [];

  const cacheKey = `search_${query}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const data = await fetchWithRetry(`${API_BASE}/search`, {
      query,
    });
    const coins = data.coins.slice(0, 10).map((coin: any) => ({
      id: coin.id,
      symbol: coin.symbol,
      name: coin.name,
      image: coin.large || coin.thumb,
      current_price: 0,
      price_change_percentage_24h: 0,
      market_cap: 0,
      total_volume: 0,
      circulating_supply: 0,
      high_24h: 0,
      low_24h: 0,
    }));
    setCache(cacheKey, coins);
    return coins;
  } catch (error) {
    console.error('Error searching coins:', error);
    return [];
  }
};

export const getGlobalData = async () => {
  const cacheKey = 'global_data';
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const data = await fetchWithRetry(`${API_BASE}/global`, {});
    setCache(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Error fetching global data:', error);
    return null;
  }
};